import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { EDialogType } from "../Interfaces/ProjectDetailInterfaces";
import axios, { AxiosResponse } from "axios";
import { t } from "i18next";
import { Common } from "../../../../common/src";
import { config } from "../../../config/config";
import {
  coordinateSystem as coordinateSystemData,
  verticalShift,
} from "../../../config/SelectListData";

type CoordinateContextType = {
  localizedStorageData: Data[];
  setLocalizedStorageData: Dispatch<SetStateAction<Data[]>>;
  storageData: Data[];
  setStorageData: Dispatch<SetStateAction<Data[]>>;
  currentType: EDataType;
  fetchCS: (projectId: string, callback?: () => void) => void;
  dashboardData: any;
  clearDashboardCS: () => void;
  fetchDashboardCS: (projectId: string) => Promise<void>;
};

const CoordinateContext = createContext({
  localizedStorageData: [],
} as CoordinateContextType);

export default CoordinateContext;

export type Data = {
  id: string;
  left: string;
  right?: string | boolean;
};

export enum EDataType {
  EPSG = "EPSG",
  LOCALIZE = "LOCALIZE",
  NONE = "NONE",
}

export const CoordinateContextProvider = (props: { children: ReactNode }) => {
  const [storageData, setStorageData] = useState<Data[]>([
    { id: "horizontal", left: t("COORDINATE_SYSTEM"), right: "" },
    { id: "vertical", left: t("VERTICAL_COORDINATE_SYSTEM"), right: "" },
  ]);

  const [localizedStorageData, setLocalizedStorageData] = useState<Data[]>([
    { id: "rotation", left: t("ROTATION"), right: "" },
    { id: "originEasting", left: t("ORIGIN_EASTING"), right: "" },
    { id: "originNorthing", left: t("ORIGIN_NORTHING"), right: "" },
    { id: "originLatitude", left: t("ORIGIN_LATITUDE"), right: "" },
    { id: "originLongitude", left: t("ORIGIN_LONGITUDE"), right: "" },
    { id: "scaleFactor", left: t("SCALE_FACTOR"), right: "" },
    { id: "verticalOffset", left: t("VERTICAL_OFFSET"), right: "" },
    { id: "inclineX", left: t("INCLINE_X"), right: "" },
    { id: "inclineY", left: t("INCLINE_Y"), right: "" },
  ]);

  const [displayText, setDisplayText] = useState<boolean>(false);
  const [currentType, setCurrentType] = useState<EDataType>(EDataType.NONE);

  const [dashboardData, setDashboardData] = useState<any>(undefined);

  const fetchCS = (projectId: string, callback?: () => void) => {
    axios
      .get(
        `${config.serverHost}/api/v1/project/${projectId}/coordinate-system`,
        { withCredentials: true }
      )
      .then((res: AxiosResponse<Common.ProjectViDoc>) => {
        setDisplayText(true);
        if (res.data.horizontal) {
          setCurrentType(EDataType.EPSG);
        } else if (res.data.originEasting) {
          setCurrentType(EDataType.LOCALIZE);
        } else {
          setCurrentType(EDataType.NONE);
          res.data.conditionFlag = false;
        }
        if (res.data.conditionFlag) {
          const newVidocData = localizedStorageData.map((item) => {
            item.right = res.data[item.id as keyof Common.ProjectViDoc];
            return item;
          });
          setLocalizedStorageData(newVidocData);
        } else {
          const newlocalizeData = storageData.map((item) => {
            item.right = (
              item.id === "horizontal" ? coordinateSystemData : verticalShift
            ).find(
              (x) => x.value === res.data[item.id as keyof Common.ProjectViDoc]
            )?.label;
            return item;
          });
          setStorageData(newlocalizeData);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const clearDashboardCS = () => {
    setDashboardData(undefined);
  };

  const fetchDashboardCS = async (projectId: string) => {
    return new Promise<void>((resolve, reject) => {
      const url = `${config.serverHost}/api/v1/project/${projectId}/coordinate-system/dashboard`;
      axios
        .get(url, { withCredentials: true })
        .then((res) => {
          // console.log("dashboardData", res.data);
          setDashboardData(res.data);
          resolve();
        })
        .catch((e) => {
          // console.error(e);
          reject();
        });
    });
  };

  const contextValue = {
    localizedStorageData,
    setLocalizedStorageData,
    storageData,
    setStorageData,
    currentType,
    fetchCS,
    dashboardData,
    clearDashboardCS,
    fetchDashboardCS,
  };

  return (
    <CoordinateContext.Provider value={contextValue}>
      {props.children}
    </CoordinateContext.Provider>
  );
};
